/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import cn from "classnames";

import { memo, ReactNode, RefObject, useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { UseMediaStreamResult } from "../../hooks/use-media-stream-mux";

import { useWebcam } from "../../hooks/use-webcam";
import { AudioRecorder } from "../../lib/audio-recorder";
import AudioPulse from "../audio-pulse/AudioPulse";
import "./control-tray.scss";

// Exposed methods for programmatic camera control
export interface ControlTrayHandle {
  startCamera: () => Promise<boolean>;
  stopCamera: () => void;
  captureFrame: () => Promise<string | null>;
  isStreaming: () => boolean;
}

/**
 * Force audio output to use the loudspeaker (not earpiece) on mobile devices
 * This prevents the audio from switching between earpiece and loudspeaker
 */
const forceAudioToLoudspeaker = async (audioElement?: HTMLAudioElement): Promise<void> => {
  try {
    // Check if we're on a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (!isMobile) {
      console.log('🔊 [Speaker] Not a mobile device, skipping loudspeaker enforcement');
      return;
    }

    console.log('🔊 [Speaker] Attempting to force audio to loudspeaker...');

    // Method 1: Use setSinkId if available (Chrome/Edge on Android)
    if (audioElement && 'setSinkId' in audioElement) {
      try {
        // Empty string means default output (loudspeaker on mobile)
        await (audioElement as any).setSinkId('');
        console.log('✅ [Speaker] setSinkId successful - audio routed to loudspeaker');
      } catch (sinkError) {
        console.warn('⚠️ [Speaker] setSinkId failed:', sinkError);
      }
    }

    // Method 2: Create a hidden audio element to lock the audio routing to loudspeaker
    if (!document.getElementById('audio-routing-anchor')) {
      const audioAnchor = document.createElement('audio');
      audioAnchor.id = 'audio-routing-anchor';
      audioAnchor.setAttribute('playsinline', 'true');
      audioAnchor.setAttribute('webkit-playsinline', 'true');
      audioAnchor.volume = 0.01; // Very low volume
      
      // Create a silent audio buffer
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0; // Silent
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Start the oscillator (this helps route audio to loudspeaker)
      oscillator.start();
      
      console.log('✅ [Speaker] Created audio routing anchor');
      
      // Keep the context alive
      setTimeout(() => {
        audioContext.close();
      }, 1000);
    }

    // Method 3: For iOS - set audio session category
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      console.log('📱 [Speaker] iOS detected - audio will use loudspeaker by default in web apps');
    }

    console.log('✅ [Speaker] Loudspeaker enforcement complete');
  } catch (error) {
    console.warn('⚠️ [Speaker] Error forcing loudspeaker:', error);
  }
};


export type ControlTrayProps = {
  videoRef: RefObject<HTMLVideoElement>;
  children?: ReactNode;
  supportsVideo: boolean;
  onVideoStreamChange?: (stream: MediaStream | null) => void;
  // Callback-based camera control for survey flow
  onCameraControlsReady?: (controls: ControlTrayHandle) => void;
};

type MediaStreamButtonProps = {
  isStreaming: boolean;
  onIcon: string;
  offIcon: string;
  start: () => Promise<any>;
  stop: () => any;
};

/**
 * button used for triggering webcam or screen-capture
 */
const MediaStreamButton = memo(
  ({ isStreaming, onIcon, offIcon, start, stop }: MediaStreamButtonProps) =>
    isStreaming ? (
      <button className="action-button" onClick={stop}>
        <span className="material-symbols-outlined">{onIcon}</span>
      </button>
    ) : (
      <button className="action-button" onClick={start}>
        <span className="material-symbols-outlined">{offIcon}</span>
      </button>
    )
);

function ControlTray({
  videoRef,
  children,
  onVideoStreamChange = () => {},
  supportsVideo,
  onCameraControlsReady,
}: ControlTrayProps) {
  const webcam = useWebcam();
  const videoStreams = [webcam];
  const [activeVideoStream, setActiveVideoStream] =
    useState<MediaStream | null>(null);
  const [inVolume, setInVolume] = useState(0);
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [muted, setMuted] = useState(false);
  const renderCanvasRef = useRef<HTMLCanvasElement>(null);
  const connectButtonRef = useRef<HTMLButtonElement>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const { client, connected, connect, disconnect, volume, audioStreamer } =
    useLiveAPIContext();

  // ═══════════════════════════════════════════════════════════
  // PROGRAMMATIC CAMERA CONTROLS FOR VEHICLE SURVEY
  // ═══════════════════════════════════════════════════════════

  const startCameraForSurvey = useCallback(async (): Promise<boolean> => {
    console.log("📹 [ControlTray] Starting camera for survey...");
    try {
      const mediaStream = await webcam.start();
      if (mediaStream) {
        setActiveVideoStream(mediaStream);
        onVideoStreamChange(mediaStream);
        console.log("✅ [ControlTray] Camera started successfully for survey");
        return true;
      }
      return false;
    } catch (error) {
      console.error("❌ [ControlTray] Failed to start camera for survey:", error);
      return false;
    }
  }, [webcam, onVideoStreamChange]);

  const stopCameraForSurvey = useCallback(() => {
    console.log("📹 [ControlTray] Stopping camera for survey...");
    webcam.stop();
    setActiveVideoStream(null);
    onVideoStreamChange(null);
    console.log("✅ [ControlTray] Camera stopped for survey");
  }, [webcam, onVideoStreamChange]);

  const captureFrameForSurvey = useCallback(async (): Promise<string | null> => {
    console.log("📸 [ControlTray] Capturing frame for survey...");
    const video = videoRef.current;
    
    if (!video || !webcam.isStreaming) {
      console.warn("⚠️ [ControlTray] Cannot capture - video not available or camera not streaming");
      return null;
    }

    try {
      // Create or reuse capture canvas
      let canvas = captureCanvasRef.current;
      if (!canvas) {
        canvas = document.createElement('canvas');
        captureCanvasRef.current = canvas;
      }

      // Set canvas to full video resolution for high quality capture
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error("❌ [ControlTray] Failed to get canvas context");
        return null;
      }

      // Draw current video frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to base64 JPEG
      const base64 = canvas.toDataURL('image/jpeg', 0.9);
      const data = base64.slice(base64.indexOf(',') + 1);

      console.log(`✅ [ControlTray] Frame captured: ${canvas.width}x${canvas.height}`);
      return data;
    } catch (error) {
      console.error("❌ [ControlTray] Failed to capture frame:", error);
      return null;
    }
  }, [videoRef, webcam.isStreaming]);

  // Use refs to always have latest function references without causing re-renders
  const cameraControlsRef = useRef<ControlTrayHandle | null>(null);
  const controlsReadyCalledRef = useRef(false);

  // Update ref with latest functions (doesn't trigger re-renders)
  cameraControlsRef.current = {
    startCamera: startCameraForSurvey,
    stopCamera: stopCameraForSurvey,
    captureFrame: captureFrameForSurvey,
    isStreaming: () => webcam.isStreaming,
  };

  // Expose camera controls to parent via callback - only once on mount
  useEffect(() => {
    if (onCameraControlsReady && !controlsReadyCalledRef.current) {
      controlsReadyCalledRef.current = true;
      
      // Create stable wrapper functions that delegate to ref
      const stableControls: ControlTrayHandle = {
        startCamera: () => cameraControlsRef.current?.startCamera() ?? Promise.resolve(false),
        stopCamera: () => cameraControlsRef.current?.stopCamera(),
        captureFrame: () => cameraControlsRef.current?.captureFrame() ?? Promise.resolve(null),
        isStreaming: () => cameraControlsRef.current?.isStreaming() ?? false,
      };
      
      onCameraControlsReady(stableControls);
      console.log("📹 [ControlTray] Camera controls exposed to parent (once)");
    }
  }, [onCameraControlsReady]);

  // Force audio to loudspeaker on mobile devices
  useEffect(() => {
    if (connected && audioStreamer) {
      // Call loudspeaker enforcement when connected
      forceAudioToLoudspeaker();
      
      // Also force it whenever the audio context state changes
      const audioContext = audioStreamer.context;
      if (audioContext) {
        const handleStateChange = () => {
          console.log('🔊 [Speaker] AudioContext state changed to:', audioContext.state);
          if (audioContext.state === 'running') {
            forceAudioToLoudspeaker();
          }
        };
        
        audioContext.addEventListener('statechange', handleStateChange);
        
        return () => {
          audioContext.removeEventListener('statechange', handleStateChange);
        };
      }
    }
  }, [connected, audioStreamer]);

  useEffect(() => {
    if (!connected && connectButtonRef.current) {
      connectButtonRef.current.focus();
    }
  }, [connected]);

  // Update activeVideoStream when webcam stream changes (e.g., after camera switching)
  useEffect(() => {
    if (webcam.isStreaming && webcam.stream) {
      console.log('📹 Updating video element with new webcam stream after camera switch');
      setActiveVideoStream(webcam.stream);
      onVideoStreamChange(webcam.stream);
    } else if (!webcam.isStreaming) {
      console.log('📹 Clearing video element - webcam stopped');
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }
  }, [webcam.stream, webcam.isStreaming, onVideoStreamChange]);
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--volume",
      `${Math.max(5, Math.min(inVolume * 200, 8))}px`
    );
  }, [inVolume]);

  // Handle audio recording - start once when connected
  useEffect(() => {
    const onData = (base64: string) => {
      client.sendRealtimeInput([
        {
          mimeType: "audio/pcm;rate=16000",
          data: base64,
        },
      ]);
    };
    
    // Start the recorder once when connected
    if (connected && audioRecorder) {
      audioRecorder.on("data", onData).on("volume", setInVolume).start();
      
      // MOBILE FIX: Ensure audio output context stays active
      if (audioStreamer) {
        const audioContext = audioStreamer.context;
        if (audioContext) {
          audioContext.resume().then(() => {
            console.log('🔊 [Mobile Fix] Audio output context resumed');
            if (audioStreamer.gainNode) {
              audioStreamer.gainNode.gain.cancelScheduledValues(audioContext.currentTime);
              audioStreamer.gainNode.gain.setValueAtTime(1.0, audioContext.currentTime);
            }
          }).catch(err => {
            console.warn('⚠️ [Mobile Fix] Failed to resume output context:', err);
          });
        }
      }
    } else if (!connected && audioRecorder) {
      // Only stop when disconnecting completely
      audioRecorder.stop();
    }
    
    return () => {
      audioRecorder.off("data", onData).off("volume", setInVolume);
    };
  }, [connected, client, audioRecorder, audioStreamer]); // Remove 'muted' from dependencies

  // Separate useEffect for applying mute state
  useEffect(() => {
    if (connected && audioRecorder && audioRecorder.micGainNode) {
      // Apply mute state via gain (no stream interruption)
      if (muted) {
        audioRecorder.muteMic();
        console.log('🔇 [Effect] Microphone muted via gain');
      } else {
        audioRecorder.unmuteMic();
        console.log('🔊 [Effect] Microphone unmuted via gain');
      }
    }
  }, [muted, connected, audioRecorder]); // Only run when mute state changes

  useEffect(() => {
    if (videoRef.current) {
      console.log('🎥 Setting video srcObject:', activeVideoStream ? 'New stream' : 'null');
      videoRef.current.srcObject = activeVideoStream;
      
      // Force video to load and play the new stream (important for Android)
      if (activeVideoStream) {
        videoRef.current.load();
        videoRef.current.play().catch((error) => {
          console.warn('📹 Video play failed (this is usually normal):', error);
        });
      }
    }

    let timeoutId = -1;

    function sendVideoFrame() {
      const video = videoRef.current;
      const canvas = renderCanvasRef.current;

      if (!video || !canvas) {
        return;
      }

      const ctx = canvas.getContext("2d")!;
      canvas.width = video.videoWidth * 0.25;
      canvas.height = video.videoHeight * 0.25;
      if (canvas.width + canvas.height > 0) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const base64 = canvas.toDataURL("image/jpeg", 1.0);
        const data = base64.slice(base64.indexOf(",") + 1, Infinity);
        client.sendRealtimeInput([{ mimeType: "image/jpeg", data }]);
      }
      if (connected) {
        timeoutId = window.setTimeout(sendVideoFrame, 1000 / 0.5);
      }
    }
    if (connected && activeVideoStream !== null) {
      requestAnimationFrame(sendVideoFrame);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [connected, activeVideoStream, client, videoRef]);

  //handler for swapping from one video-stream to the next
  const changeStreams = (next?: UseMediaStreamResult) => async () => {
    if (next) {
      const mediaStream = await next.start();
      setActiveVideoStream(mediaStream);
      onVideoStreamChange(mediaStream);
    } else {
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }

    videoStreams.filter((msr) => msr !== next).forEach((msr) => msr.stop());
  };

  // Handle mute/unmute with gain-based control (no stream interruption)
  const handleMuteToggle = async () => {
    const newMutedState = !muted;
    setMuted(newMutedState);
    
    // Apply mute/unmute via gain (instant, no stream restart)
    if (newMutedState) {
      audioRecorder.muteMic();
      console.log('🔇 [Mute] Microphone muted via gain');
    } else {
      audioRecorder.unmuteMic();
      console.log('🔊 [Unmute] Microphone unmuted via gain');
    }
    
    // MOBILE FIX: Force audio to loudspeaker on every toggle
    await forceAudioToLoudspeaker();
    
    // MOBILE FIX: Ensure output audio context stays active and at full volume
    if (audioStreamer) {
      try {
        const audioContext = audioStreamer.context;
        if (audioContext) {
          // Resume if suspended
          if (audioContext.state !== 'running') {
            console.log('🔊 [Mobile Fix] Resuming AudioContext via user interaction');
            await audioContext.resume();
          }
          
          // Force output gain to maximum
          if (audioStreamer.gainNode) {
            audioStreamer.gainNode.gain.cancelScheduledValues(audioContext.currentTime);
            audioStreamer.gainNode.gain.setValueAtTime(1.0, audioContext.currentTime);
            console.log('🔊 [Mobile Fix] Output gain locked to 1.0');
          }
        }
      } catch (error) {
        console.warn('⚠️ [Mobile Fix] Error in handleMuteToggle:', error);
      }
    }
  };

  return (
    <section className="control-tray">
      <canvas style={{ display: "none" }} ref={renderCanvasRef} />
      <nav className={cn("actions-nav", { disabled: !connected, hidden: !connected })}>
        <button
          className={cn("action-button mic-button")}
          onClick={handleMuteToggle}
        >
          {!muted ? (
            <span className="material-symbols-outlined filled">mic</span>
          ) : (
            <span className="material-symbols-outlined filled">mic_off</span>
          )}
        </button>

        {supportsVideo && (
          <>
            <MediaStreamButton
              isStreaming={webcam.isStreaming}
              start={changeStreams(webcam)}
              stop={changeStreams()}
              onIcon="videocam_off"
              offIcon="videocam"
            />
            {/* Camera switch button - only show when webcam is active and multiple cameras available */}
            {webcam.isStreaming && webcam.canSwitchCamera && (
              <button
                className={cn("action-button camera-switch", { 
                  switching: webcam.isSwitching 
                })}
                onClick={webcam.switchCamera}
                disabled={webcam.isSwitching}
                title={webcam.isSwitching 
                  ? 'Switching camera...' 
                  : `Switch to ${webcam.facingMode === 'user' ? 'back' : 'front'} camera`
                }
              >
                {webcam.isSwitching ? (
                  <span className="material-symbols-outlined rotating">
                    sync
                  </span>
                ) : (
                  <span className="material-symbols-outlined">
                    {webcam.facingMode === 'user' ? 'camera_rear' : 'camera_front'}
                  </span>
                )}
              </button>
            )}
          </>
        )}
        
        <div className="action-button no-action outlined">
          <AudioPulse volume={volume} active={connected} hover={false} />
        </div>
        
        {children}
      </nav>

      <div className={cn("connection-container", { connected })}>
        <div className="connection-button-container">
          <button
            ref={connectButtonRef}
            className={cn("action-button connect-toggle", { connected })}
            onClick={connected ? disconnect : connect}
          >
            {connected ? (
            <img src="/animationPause.gif" alt="" style={{width: '90px', height: '90px',zIndex: 1}} />
          ) : (
            <span className=" filled"><img src="microphone.png" width="23" /></span>
          )}
            {/* <span className="material-symbols-outlined filled">
              {connected ? "pause" : "play_arrow"}
            </span> */}
          </button>
        </div>
        <span className="text-indicator">Streaming</span>
      </div>
    </section>
  );
}

export default memo(ControlTray);
